import { Link, useLocation } from '@tanstack/react-router'
import { cn } from '@/lib/utils'

// Recreated brand mark — a tri-colour swirl evoking the client's logo.
// Swap for the real asset when provided.
function BrandMark() {
  return (
    <svg viewBox="0 0 32 32" className="h-7 w-7 shrink-0" aria-hidden="true">
      <circle cx="16" cy="16" r="14.5" fill="none" stroke="#5FA63C" strokeWidth="1.4" opacity="0.5" />
      <g fill="none" strokeWidth="3.6" strokeLinecap="round">
        <path d="M16 5 A 11 11 0 0 1 25.5 11" stroke="#155DA8" />
        <path d="M16 5 A 11 11 0 0 1 25.5 11" stroke="#E0512E" transform="rotate(120 16 16)" />
        <path d="M16 5 A 11 11 0 0 1 25.5 11" stroke="#5FA63C" transform="rotate(240 16 16)" />
      </g>
    </svg>
  )
}

interface NavItem {
  label: string
  href: string
  icon: React.ReactNode
}

interface NavSection {
  title?: string
  items: NavItem[]
}

interface SidebarProps {
  /**
   * Navigation sections with items
   */
  sections: NavSection[]
  /**
   * Whether sidebar is collapsed (icon-only mode)
   */
  collapsed?: boolean
  /**
   * Callback to toggle collapsed state
   */
  onToggle?: () => void
  /**
   * Current tenant name for display
   */
  tenantName?: string
  /**
   * User info for bottom section
   */
  user?: {
    name: string
    email: string
    avatarUrl?: string
  }
  /**
   * Logo element
   */
  logo?: React.ReactNode
}

/**
 * Sidebar navigation following PRD guidelines:
 * - Max 7±2 primary navigation items
 * - Group related items with section headers
 * - Current page clearly highlighted
 * - Icons + labels (never icons only, unless collapsed)
 * - 240px expanded, 64px collapsed
 *
 * @example
 * <Sidebar
 *   sections={[
 *     {
 *       items: [
 *         { label: 'Dashboard', href: '/', icon: <HomeIcon /> },
 *         { label: 'Projects', href: '/projects', icon: <FolderIcon /> },
 *       ]
 *     },
 *     {
 *       title: 'WORKSPACE',
 *       items: [
 *         { label: 'Team', href: '/team', icon: <UsersIcon /> },
 *         { label: 'Settings', href: '/settings', icon: <SettingsIcon /> },
 *       ]
 *     }
 *   ]}
 *   tenantName="Acme Corp"
 *   user={{ name: 'John Doe', email: 'john@acme.com' }}
 * />
 */
export function Sidebar({
  sections,
  collapsed = false,
  onToggle,
  tenantName,
  user,
  logo,
}: SidebarProps) {
  const location = useLocation()

  return (
    <aside
      className={cn(
        'flex flex-col h-screen bg-card border-r transition-all duration-200',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      {/* Logo / Tenant */}
      <div className={cn('h-[60px] flex items-center gap-2.5 border-b', collapsed ? 'justify-center px-2' : 'px-4')}>
        {logo || (
          <>
            <BrandMark />
            {!collapsed && (
              <div className="min-w-0 leading-tight">
                <div className="line-clamp-2 text-[13px] font-semibold leading-tight">
                  {tenantName || 'TalentForge'}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {sections.map((section, sectionIndex) => (
          <div key={section.title ?? `section-${sectionIndex}`} className="mb-4">
            {/* Section Title */}
            {section.title && !collapsed && (
              <div className="px-4 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {section.title}
              </div>
            )}

            {/* Nav Items */}
            <ul className="space-y-1 px-2">
              {section.items.map((item) => {
                const isActive = location.pathname === item.href

                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                        'hover:bg-accent hover:text-accent-foreground',
                        isActive
                          ? 'bg-accent text-accent-foreground font-medium'
                          : 'text-muted-foreground',
                        collapsed && 'justify-center'
                      )}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="flex-shrink-0">{item.icon}</span>
                      {!collapsed && <span>{item.label}</span>}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Toggle Button */}
      {onToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="p-2 mx-2 mb-2 rounded hover:bg-accent text-muted-foreground"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            className={cn('h-5 w-5 transition-transform', collapsed && 'rotate-180')}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
            role="presentation"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          </svg>
        </button>
      )}

      {/* User Section */}
      {user && (
        <div className={cn('border-t p-4', collapsed && 'flex justify-center')}>
          <div className={cn('flex items-center gap-3', collapsed && 'justify-center')}>
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-medium">{user.name[0].toUpperCase()}</span>
              )}
            </div>

            {/* User Info */}
            {!collapsed && (
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{user.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  )
}
